import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Award, Lightbulb, Leaf, Target } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We operate with honesty, transparency and accountability.'
    },
    {
      icon: Award,
      title: 'Quality',
      description: 'We are committed to high standards of workmanship and delivery.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We embrace better technologies and approaches.'
    },
    {
      icon: Shield,
      title: 'Safety',
      description: 'We prioritize safe project environments.'
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'We promote responsible and environmentally conscious practices.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About Us - BURALE Maintainance & Suppliers | Our Story & Values</title>
        <meta name="description" content="Learn about BURALE Maintainance and Suppliers Limited - A Kenyan construction and project management company committed to quality, innovation and sustainable project delivery." />
      </Helmet>
      
      <section className="relative h-96 flex items-center justify-center bg-gradient-to-br from-burale-dark to-burale-teal">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">ABOUT BURALE</h1>
          <p className="text-2xl text-burale-gold font-semibold mb-4">Building Solutions. Delivering Value.</p>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            A construction and project management company committed to quality, innovation and sustainable project delivery.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="section-heading text-center mb-12">Who We Are</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Burale Maintainance and Suppliers Limited is a Kenyan construction and project management company providing integrated solutions across building construction, civil works, infrastructure, water projects, electrical works, consultancy and supplies.
              </p>
              <p>
                Our approach combines technical expertise, project coordination and practical construction experience to deliver solutions aligned with our clients' requirements.
              </p>
              <p>
                We are dedicated to high-quality, innovative and sustainable building solutions, with experience spanning commercial, residential, industrial construction and renovation projects.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-teal text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6">
              <Target className="text-white" size={40} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-8">OUR MISSION</h2>
            <p className="text-xl md:text-2xl leading-relaxed">
              To provide efficient and effective Project and Construction Management services meeting or exceeding the expectations of our clients while satisfying the needs and ambitions of our shareholders and staff.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-burale-teal rounded-full mb-6">
              <Lightbulb className="text-white" size={40} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-burale-gold">OUR ASPIRATION</h2>
            <p className="text-xl md:text-2xl leading-relaxed text-gray-300">
              To be a trusted partner in delivering quality construction, infrastructure and project solutions that create lasting value for our clients and communities.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading">OUR VALUES</h2>
            <p className="section-subheading">The principles that guide our work and relationships</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-burale-teal/10 rounded-full mb-6 group-hover:bg-burale-teal transition-all duration-300">
                    <Icon className="text-burale-teal group-hover:text-white transition-all duration-300" size={48} />
                  </div>
                  <h3 className="text-2xl font-bold text-burale-dark mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading">OUR CAPABILITIES</h2>
            <p className="section-subheading">Built on Experience and Professional Practice</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-burale-teal/10 rounded-lg flex items-center justify-center mb-6">
                <span className="text-3xl font-bold text-burale-teal">01</span>
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Construction Capability</h3>
              <p className="text-gray-600 leading-relaxed">
                Building and specialized construction projects across commercial, residential, and industrial sectors.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-burale-teal/10 rounded-lg flex items-center justify-center mb-6">
                <span className="text-3xl font-bold text-burale-teal">02</span>
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Civil Engineering Capability</h3>
              <p className="text-gray-600 leading-relaxed">
                Civil infrastructure and road-related works including earthworks and surfacing.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-burale-teal/10 rounded-lg flex items-center justify-center mb-6">
                <span className="text-3xl font-bold text-burale-teal">03</span>
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Water Infrastructure</h3>
              <p className="text-gray-600 leading-relaxed">
                Dams, boreholes and water infrastructure development projects.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-burale-teal/10 rounded-lg flex items-center justify-center mb-6">
                <span className="text-3xl font-bold text-burale-teal">04</span>
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Project Management</h3>
              <p className="text-gray-600 leading-relaxed">
                Project planning, coordination and delivery management services.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-burale-teal/10 rounded-lg flex items-center justify-center mb-6">
                <span className="text-3xl font-bold text-burale-teal">05</span>
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Procurement & Supplies</h3>
              <p className="text-gray-600 leading-relaxed">
                Sourcing and supply of required materials and equipment.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-burale-teal/10 rounded-lg flex items-center justify-center mb-6">
                <span className="text-3xl font-bold text-burale-teal">06</span>
              </div>
              <h3 className="text-2xl font-bold text-burale-dark mb-4">Technical Resources</h3>
              <p className="text-gray-600 leading-relaxed">
                Access to construction equipment and technical personnel for project execution.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
